import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Alert,
  Card,
  CardMedia,
  ImageList,
  ImageListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getArtworksListPath } from '../utils/listViewState';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import ArtworkTagsEditor from '../components/ArtworkTagsEditor';

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [legendMenuAnchor, setLegendMenuAnchor] = useState(null);
  const [actMenuAnchor, setActMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // Выставки: диалог добавления/редактирования
  const [exhibitionDialogOpen, setExhibitionDialogOpen] = useState(false);
  const [exhibitionEditId, setExhibitionEditId] = useState(null);
  const [exhibitionForm, setExhibitionForm] = useState({
    exhibition_name: '',
    exhibition_year: new Date().getFullYear(),
    exhibition_city: '',
    exhibition_date_start: '',
    exhibition_date_end: '',
    exhibition_type: '',
    notes: '',
  });
  const [exhibitionSubmitLoading, setExhibitionSubmitLoading] = useState(false);
  const [exhibitionDeleteLoading, setExhibitionDeleteLoading] = useState(null);
  // Акт «Выставка»: диалог выбора/ввода выставки
  const [actExhibitionDialogOpen, setActExhibitionDialogOpen] = useState(false);
  const [actExhibitionChoice, setActExhibitionChoice] = useState('existing');
  const [actExhibitionId, setActExhibitionId] = useState('');
  const [actExhibitionNew, setActExhibitionNew] = useState({
    exhibition_name: '',
    exhibition_year: new Date().getFullYear(),
    exhibition_city: '',
    exhibition_date_start: '',
    exhibition_date_end: '',
    exhibition_type: '',
  });
  const [actGenerateLoading, setActGenerateLoading] = useState(false);

  const { data: artwork, isLoading, refetch } = useQuery(
    ['artwork', id],
    async () => {
      const response = await axios.get(`/api/artworks/${id}`);
      return response.data;
    }
  );

  const handleExportLegend = async (format) => {
    try {
      const response = await axios.get(`/api/artworks/${id}/legend?format=${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `legend_${artwork.inventory_number}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setLegendMenuAnchor(null);
    } catch (error) {
      console.error('Ошибка экспорта легенды:', error);
    }
  };

  const handleOpenLabel = async () => {
    try {
      const response = await axios.get(`/api/artworks/${id}/label`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (!blob.size) {
        throw new Error('Пустой файл');
      }
      const url = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `label_${artwork?.inventory_number || id}.pdf`;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Ошибка открытия этикетки:', error);
    }
  };

  const handleGenerateAct = async (actType) => {
    setActMenuAnchor(null);
    if (actType === 'выставка') {
      setActExhibitionChoice(artwork.exhibitions?.length ? 'existing' : 'new');
      setActExhibitionId(artwork.exhibitions?.length ? String(artwork.exhibitions[0].id) : '');
      setActExhibitionNew({
        exhibition_name: '',
        exhibition_year: new Date().getFullYear(),
        exhibition_city: '',
        exhibition_date_start: '',
        exhibition_date_end: '',
        exhibition_type: '',
      });
      setActExhibitionDialogOpen(true);
      return;
    }
    await doGenerateAct(actType, null);
  };

  const doGenerateAct = async (actType, exhibitionPayload) => {
    setActGenerateLoading(true);
    try {
      const body = { act_type: actType, ...exhibitionPayload };
      const response = await axios.post(
        `/api/artworks/${id}/internal-act`,
        body,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `act_${artwork.inventory_number}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setActExhibitionDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Ошибка генерации акта:', error);
    } finally {
      setActGenerateLoading(false);
    }
  };

  const handleActExhibitionSubmit = () => {
    if (actExhibitionChoice === 'existing' && actExhibitionId) {
      doGenerateAct('выставка', { exhibition_id: parseInt(actExhibitionId, 10) });
      return;
    }
    if (actExhibitionChoice === 'new') {
      const { exhibition_name, exhibition_city, exhibition_year } = actExhibitionNew;
      if (!exhibition_name?.trim() || !exhibition_city?.trim()) {
        setErrorMessage('Укажите название и город мероприятия');
        return;
      }
      doGenerateAct('выставка', {
        exhibition_name: exhibition_name.trim(),
        exhibition_city: exhibition_city.trim(),
        exhibition_year: exhibition_year || new Date().getFullYear(),
        exhibition_date_start: actExhibitionNew.exhibition_date_start || undefined,
        exhibition_date_end: actExhibitionNew.exhibition_date_end || undefined,
        exhibition_type: actExhibitionNew.exhibition_type || undefined,
      });
    }
  };

  const openAddExhibitionDialog = () => {
    setExhibitionEditId(null);
    setExhibitionForm({
      exhibition_name: '',
      exhibition_year: new Date().getFullYear(),
      exhibition_city: '',
      exhibition_date_start: '',
      exhibition_date_end: '',
      exhibition_type: '',
      notes: '',
    });
    setExhibitionDialogOpen(true);
  };

  const openEditExhibitionDialog = (ex) => {
    setExhibitionEditId(ex.id);
    setExhibitionForm({
      exhibition_name: ex.exhibition_name || '',
      exhibition_year: ex.exhibition_year || new Date().getFullYear(),
      exhibition_city: ex.exhibition_city || '',
      exhibition_date_start: ex.exhibition_date_start || '',
      exhibition_date_end: ex.exhibition_date_end || '',
      exhibition_type: ex.exhibition_type || '',
      notes: ex.notes || '',
    });
    setExhibitionDialogOpen(true);
  };

  const handleExhibitionDialogSubmit = async () => {
    if (!exhibitionForm.exhibition_name?.trim() || !exhibitionForm.exhibition_city?.trim()) {
      setErrorMessage('Укажите название и город');
      return;
    }
    setExhibitionSubmitLoading(true);
    setErrorMessage(null);
    try {
      const payload = {
        exhibition_name: exhibitionForm.exhibition_name.trim(),
        exhibition_city: exhibitionForm.exhibition_city.trim(),
        exhibition_year: Number(exhibitionForm.exhibition_year) || new Date().getFullYear(),
      };
      if (exhibitionForm.exhibition_date_start) payload.exhibition_date_start = exhibitionForm.exhibition_date_start;
      if (exhibitionForm.exhibition_date_end) payload.exhibition_date_end = exhibitionForm.exhibition_date_end;
      if (exhibitionForm.exhibition_type) payload.exhibition_type = exhibitionForm.exhibition_type;
      if (exhibitionForm.notes) payload.notes = exhibitionForm.notes;

      if (exhibitionEditId) {
        await axios.put(`/api/exhibitions/${exhibitionEditId}`, payload);
      } else {
        await axios.post(`/api/artworks/${id}/exhibitions`, payload);
      }
      setExhibitionDialogOpen(false);
      refetch();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Не удалось сохранить выставку');
    } finally {
      setExhibitionSubmitLoading(false);
    }
  };

  const handleDeleteExhibition = async (exId) => {
    setExhibitionDeleteLoading(exId);
    setErrorMessage(null);
    try {
      await axios.delete(`/api/exhibitions/${exId}`);
      refetch();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Не удалось удалить запись');
    } finally {
      setExhibitionDeleteLoading(null);
    }
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setErrorMessage(null);
    try {
      await axios.delete(`/api/artworks/${id}`);
      setDeleteDialogOpen(false);
      navigate(getArtworksListPath());
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Не удалось удалить произведение');
      setDeleteLoading(false);
    }
  };

  const handleCreateCopy = async () => {
    setCopyLoading(true);
    setErrorMessage(null);
    try {
      const response = await axios.post(`/api/artworks/${id}/copy`);
      const newArtwork = response.data;
      navigate(`/artworks/${newArtwork.id}`);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Не удалось создать копию');
    } finally {
      setCopyLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!artwork) {
    return <Alert severity="error">Произведение не найдено</Alert>;
  }

  return (
    <Box>
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {artwork.inventory_number} — {artwork.artwork_title}
        </Typography>
        <Box>
          {hasPermission(user, 'edit_artworks') && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate(`/artworks/${id}/edit`)}
              sx={{ mr: 1 }}
            >
              Редактировать
            </Button>
          )}
          {hasPermission(user, 'create_artworks') && (
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={handleCreateCopy}
              disabled={copyLoading}
              sx={{ mr: 1 }}
            >
              {copyLoading ? 'Создание…' : 'Создать копию'}
            </Button>
          )}
          {hasPermission(user, 'export_documents') && (
            <>
          <Button
            startIcon={<LocalOfferIcon />}
            onClick={handleOpenLabel}
            sx={{ mr: 1 }}
          >
            Этикетка для печати
          </Button>
          <Button
            startIcon={<DescriptionIcon />}
            onClick={(e) => setLegendMenuAnchor(e.currentTarget)}
            sx={{ mr: 1 }}
          >
            Экспорт легенды
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={(e) => setActMenuAnchor(e.currentTarget)}
            sx={{ mr: 1 }}
          >
            Внутренний акт
          </Button>
            </>
          )}
          {hasPermission(user, 'delete_artworks') && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={deleteLoading}
          >
            Удалить
          </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Теги
        </Typography>
        <ArtworkTagsEditor
          artworkId={artwork.id}
          tags={artwork.tags}
          editable={hasPermission(user, 'edit_artworks')}
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Удалить произведение?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить произведение «{artwork.artwork_title}»? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Удаление…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={legendMenuAnchor}
        open={Boolean(legendMenuAnchor)}
        onClose={() => setLegendMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExportLegend('txt')}>TXT</MenuItem>
        <MenuItem onClick={() => handleExportLegend('docx')}>DOCX</MenuItem>
        <MenuItem onClick={() => handleExportLegend('html')}>HTML</MenuItem>
        <MenuItem onClick={() => handleExportLegend('json')}>JSON</MenuItem>
      </Menu>

      <Menu
        anchorEl={actMenuAnchor}
        open={Boolean(actMenuAnchor)}
        onClose={() => setActMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleGenerateAct('экспозиция')}>
          Экспозиция FUTURO
        </MenuItem>
        <MenuItem onClick={() => handleGenerateAct('выставка')}>
          Выставка/Ярмарка
        </MenuItem>
        <MenuItem onClick={() => handleGenerateAct('возврат')}>
          Возврат в фондохранилище
        </MenuItem>
      </Menu>

      {/* Диалог добавления/редактирования выставки */}
      <Dialog open={exhibitionDialogOpen} onClose={() => setExhibitionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{exhibitionEditId ? 'Редактировать запись о выставке' : 'Добавить выставку'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Название мероприятия"
              value={exhibitionForm.exhibition_name}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Город"
              value={exhibitionForm.exhibition_city}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_city: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Год"
              type="number"
              value={exhibitionForm.exhibition_year}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_year: e.target.value }))}
              fullWidth
              inputProps={{ min: 1900, max: 2100 }}
            />
            <TextField
              label="Дата начала (дд.мм.гггг)"
              placeholder="дд.мм.гггг"
              value={exhibitionForm.exhibition_date_start}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_date_start: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Дата окончания (дд.мм.гггг)"
              placeholder="дд.мм.гггг"
              value={exhibitionForm.exhibition_date_end}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_date_end: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Тип (ярмарка, выставка, биеннале)"
              value={exhibitionForm.exhibition_type}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, exhibition_type: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Примечания"
              value={exhibitionForm.notes}
              onChange={(e) => setExhibitionForm((f) => ({ ...f, notes: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExhibitionDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleExhibitionDialogSubmit} disabled={exhibitionSubmitLoading}>
            {exhibitionSubmitLoading ? 'Сохранение…' : (exhibitionEditId ? 'Сохранить' : 'Добавить')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выбора выставки для акта */}
      <Dialog open={actExhibitionDialogOpen} onClose={() => !actGenerateLoading && setActExhibitionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Данные о выставке для акта</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={actExhibitionChoice}
            onChange={(e) => setActExhibitionChoice(e.target.value)}
            sx={{ mt: 1 }}
          >
            {artwork.exhibitions?.length > 0 && (
              <FormControlLabel value="existing" control={<Radio />} label="Выбрать существующую запись" />
            )}
            <FormControlLabel value="new" control={<Radio />} label="Ввести новые данные (запись будет добавлена)" />
          </RadioGroup>
          {actExhibitionChoice === 'existing' && artwork.exhibitions?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Выставка</Typography>
              <RadioGroup
                value={actExhibitionId}
                onChange={(e) => setActExhibitionId(e.target.value)}
              >
                {artwork.exhibitions.map((ex) => (
                  <FormControlLabel
                    key={ex.id}
                    value={String(ex.id)}
                    control={<Radio />}
                    label={`${ex.exhibition_name}, ${ex.exhibition_year}, ${ex.exhibition_city}`}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
          {actExhibitionChoice === 'new' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Название мероприятия"
                value={actExhibitionNew.exhibition_name}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_name: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Город"
                value={actExhibitionNew.exhibition_city}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_city: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Год"
                type="number"
                value={actExhibitionNew.exhibition_year}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_year: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Дата начала (дд.мм.гггг)"
                placeholder="дд.мм.гггг"
                value={actExhibitionNew.exhibition_date_start}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_date_start: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Дата окончания (дд.мм.гггг)"
                placeholder="дд.мм.гггг"
                value={actExhibitionNew.exhibition_date_end}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_date_end: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Тип"
                value={actExhibitionNew.exhibition_type}
                onChange={(e) => setActExhibitionNew((f) => ({ ...f, exhibition_type: e.target.value }))}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActExhibitionDialogOpen(false)} disabled={actGenerateLoading}>Отмена</Button>
          <Button variant="contained" onClick={handleActExhibitionSubmit} disabled={actGenerateLoading}>
            {actGenerateLoading ? 'Генерация…' : 'Сгенерировать акт'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Основная информация" />
          <Tab label="Выставки" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ mt: 3 }}>
            {artwork.images && artwork.images.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Изображения произведения
                </Typography>
                <ImageList cols={3} gap={16} sx={{ mb: 2 }}>
                  {artwork.images.map((image) => (
                    <ImageListItem key={image.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          image={`/uploads/${image.file_path}`}
                          alt={image.file_name}
                          sx={{
                            height: 300,
                            objectFit: 'contain',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => {
                            // Открытие изображения в полном размере
                            window.open(`/uploads/${image.file_path}`, '_blank');
                          }}
                        />
                      </Card>
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Автор
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.author?.full_name || 'Неизвестно'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Коллекция
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.collection_code}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Год создания
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.creation_year_display || artwork.creation_year || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Техника исполнения
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.material_and_technique}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Описание
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.artwork_description}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Размеры
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.height_cm &&
                    `${artwork.height_cm} × ${artwork.width_cm || ''} × ${
                      artwork.depth_cm || ''
                    } см`}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Состояние
                </Typography>
                <Chip label={artwork.condition} size="small" sx={{ mt: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Статус
                </Typography>
                <Chip label={artwork.status} color="primary" size="small" sx={{ mt: 1 }} />
              </Grid>
              {hasPermission(user, 'view_sensitive_fields') && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Стоимость покупки
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {artwork.agreed_price
                    ? new Intl.NumberFormat('ru-RU').format(artwork.agreed_price) +
                      ` ${artwork.purchase_currency || 'RUB'}`
                    : 'Не указана'}
                </Typography>
              </Grid>
              )}
              {artwork.acquisition_place && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Место приобретения
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {artwork.acquisition_place}
                  </Typography>
                </Grid>
              )}
              {hasPermission(user, 'view_sensitive_fields') && (artwork.sales_price != null && artwork.sales_price !== '') && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Фактическая стоимость продажи
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Intl.NumberFormat('ru-RU').format(artwork.sales_price) +
                      ` ${artwork.sales_currency || 'RUB'}`}
                  </Typography>
                </Grid>
              )}
              {artwork.storage_place && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Место создания
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {artwork.storage_place}
                  </Typography>
                </Grid>
              )}
              {hasPermission(user, 'view_sensitive_fields') && artwork.buying_collector && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Купивший коллекционер
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {artwork.buying_collector}
                  </Typography>
                </Grid>
              )}
              {artwork.storage_location && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Локация хранения
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {artwork.storage_location}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Участие в выставках</Typography>
              {hasPermission(user, 'edit_artworks') && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={openAddExhibitionDialog}
              >
                Добавить выставку
              </Button>
              )}
            </Box>
            {artwork.exhibitions && artwork.exhibitions.length > 0 ? (
              artwork.exhibitions.map((ex) => (
                <Paper key={ex.id} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6">{ex.exhibition_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ex.exhibition_year}, {ex.exhibition_city}
                    </Typography>
                    {(ex.exhibition_date_start || ex.exhibition_date_end) && (
                      <Typography variant="body2" color="text.secondary">
                        {ex.exhibition_date_start && `С ${ex.exhibition_date_start}`}
                        {ex.exhibition_date_end && ` по ${ex.exhibition_date_end}`}
                      </Typography>
                    )}
                    {ex.exhibition_type && (
                      <Typography variant="body2" color="text.secondary">
                        Тип: {ex.exhibition_type}
                      </Typography>
                    )}
                    {ex.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {ex.notes}
                      </Typography>
                    )}
                  </Box>
                  {hasPermission(user, 'edit_artworks') && (
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openEditExhibitionDialog(ex)}
                      title="Редактировать"
                      sx={{ mr: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteExhibition(ex.id)}
                      disabled={exhibitionDeleteLoading === ex.id}
                      title="Удалить"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  )}
                </Paper>
              ))
            ) : (
              <Typography color="text.secondary">Нет записей о выставках. Добавьте вручную или при генерации акта «Выставка/Ярмарка».</Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

